import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, X, Save, ArrowLeft, Shield, Eye, UserCog } from 'lucide-react';
import AdminNav from '../components/AdminNav';
import { useLanguage } from '../i18n/LanguageContext';
import { useAdminAuth } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const apiFetch = async (path, options = {}) => {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include', ...options });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const AdminUsersPage = () => {
  const { t } = useLanguage();
  const auth = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=list, 'new', or user.id
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ username: '', password: '', display_name: '', access_type: 'full_access', max_logins: '', max_concurrent: '', expires_days: '' });

  const fetchUsers = useCallback(async () => {
    try { setLoading(true); const data = await apiFetch('/api/admin-users'); setUsers(data.items || []); }
    catch (err) { setError(err.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  if (auth.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="admin-users-page">
        <AdminNav />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-700 mb-2">Super Admin Only</h1>
          <p className="text-sm text-slate-400">Only the Super Admin can manage admin users.</p>
        </div>
      </div>
    );
  }

  const startEdit = (user) => {
    if (user) {
      setEditing(user.id);
      setForm({ username: user.username, password: '', display_name: user.display_name || '', access_type: user.access_type, max_logins: user.max_logins ?? '', max_concurrent: user.max_concurrent ?? '', expires_days: '' });
    } else {
      setEditing('new');
      setForm({ username: '', password: '', display_name: '', access_type: 'full_access', max_logins: '', max_concurrent: '', expires_days: '' });
    }
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      if (editing === 'new') {
        if (!form.username || !form.password) { setError('Username and password required'); setSaving(false); return; }
        await apiFetch('/api/admin-users', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...form,
            max_logins: form.max_logins ? parseInt(form.max_logins) : null,
            max_concurrent: form.max_concurrent ? parseInt(form.max_concurrent) : null,
            expires_days: form.expires_days ? parseInt(form.expires_days) : null
          })
        });
      } else {
        const updates = { display_name: form.display_name, access_type: form.access_type };
        if (form.password) updates.password = form.password;
        if (form.max_logins !== '') updates.max_logins = parseInt(form.max_logins) || null;
        if (form.max_concurrent !== '') updates.max_concurrent = parseInt(form.max_concurrent) || null;
        await apiFetch(`/api/admin-users/${editing}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates)
        });
      }
      setEditing(null); fetchUsers();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete admin user "${user.display_name || user.username}"?`)) return;
    try { await apiFetch(`/api/admin-users/${user.id}`, { method: 'DELETE' }); fetchUsers(); } catch (err) { setError(err.message); }
  };

  const handleToggleDisable = async (user) => {
    try {
      await apiFetch(`/api/admin-users/${user.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ disabled: !user.disabled })
      });
      fetchUsers();
    } catch (err) { setError(err.message); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-users-page">
      <AdminNav />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
            {error} <button onClick={() => setError('')} className="float-right"><X className="w-4 h-4" /></button>
          </div>
        )}

        {editing !== null ? (
          <div className="space-y-4" data-testid="admin-user-editor">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setEditing(null)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
                <h1 className="text-xl font-bold text-slate-900">{editing === 'new' ? 'Create Admin User' : 'Edit Admin User'}</h1>
              </div>
              <button onClick={handleSave} disabled={saving} data-testid="save-user-btn"
                className="flex items-center gap-2 px-5 py-2.5 bg-[#0077FF] hover:bg-[#0066DD] disabled:bg-slate-300 text-white text-sm font-semibold rounded-xl">
                <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Username</label>
                  <input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                    disabled={editing !== 'new'} data-testid="user-username-input"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm disabled:opacity-50" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{editing === 'new' ? 'Password' : 'New Password (optional)'}</label>
                  <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    data-testid="user-password-input"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Display Name</label>
                <input value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                  data-testid="user-display-name-input"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Access Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setForm(f => ({ ...f, access_type: 'full_access' }))} data-testid="access-type-full"
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-colors ${
                      form.access_type === 'full_access' ? 'border-[#0077FF] bg-blue-50 text-[#0077FF]' : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}>
                    <UserCog className="w-4 h-4" /> Full Access
                  </button>
                  <button onClick={() => setForm(f => ({ ...f, access_type: 'visual_only' }))} data-testid="access-type-visual"
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-colors ${
                      form.access_type === 'visual_only' ? 'border-amber-500 bg-amber-50 text-amber-600' : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}>
                    <Eye className="w-4 h-4" /> Visual / Demo Only
                  </button>
                </div>
              </div>
              {editing === 'new' && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Max Logins</label>
                    <input type="number" value={form.max_logins} onChange={e => setForm(f => ({ ...f, max_logins: e.target.value }))}
                      placeholder="Unlimited" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Max Concurrent</label>
                    <input type="number" value={form.max_concurrent} onChange={e => setForm(f => ({ ...f, max_concurrent: e.target.value }))}
                      placeholder="Unlimited" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Expires (days)</label>
                    <input type="number" value={form.expires_days} onChange={e => setForm(f => ({ ...f, expires_days: e.target.value }))}
                      placeholder="Never" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-users-list">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-slate-900">Admin Users</h1>
                <p className="text-sm text-slate-500 mt-0.5">Manage administrator accounts and access levels</p>
              </div>
              <button onClick={() => startEdit(null)} data-testid="create-admin-user-btn"
                className="flex items-center gap-2 px-4 py-2.5 bg-[#0077FF] hover:bg-[#0066DD] text-white text-sm font-semibold rounded-xl">
                <Plus className="w-4 h-4" /> New Admin
              </button>
            </div>

            {/* Super Admin card */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 border border-slate-700 rounded-xl px-5 py-4 flex items-center justify-between" data-testid="super-admin-card">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Shield className="w-4.5 h-4.5 text-amber-400" />
                </div>
                <div>
                  <p className="font-bold text-white text-sm">Super Admin</p>
                  <p className="text-xs text-slate-400">Unlimited access | No restrictions</p>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-semibold uppercase tracking-wider">Owner</span>
            </div>

            {loading ? <div className="text-center py-16 text-slate-400">Loading...</div> : users.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-2xl">
                <UserCog className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 text-sm mb-1">No admin users created yet</p>
                <p className="text-slate-300 text-xs">Create an admin account to delegate access</p>
              </div>
            ) : (
              <div className="space-y-2">
                {users.map(user => (
                  <div key={user.id} className="bg-white border border-slate-200 rounded-xl px-5 py-3.5 flex items-center justify-between group" data-testid={`admin-user-row-${user.id}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                        user.disabled ? 'bg-slate-100' : user.access_type === 'full_access' ? 'bg-blue-50' : 'bg-amber-50'
                      }`}>
                        {user.access_type === 'full_access'
                          ? <UserCog className={`w-4.5 h-4.5 ${user.disabled ? 'text-slate-400' : 'text-[#0077FF]'}`} />
                          : <Eye className={`w-4.5 h-4.5 ${user.disabled ? 'text-slate-400' : 'text-amber-600'}`} />
                        }
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`font-semibold text-sm ${user.disabled ? 'text-slate-400' : 'text-slate-800'}`}>{user.display_name || user.username}</p>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            user.disabled ? 'bg-slate-100 text-slate-400' :
                            user.access_type === 'full_access' ? 'bg-blue-50 text-[#0077FF]' : 'bg-amber-50 text-amber-600'
                          }`}>
                            {user.disabled ? 'Disabled' : user.access_type === 'full_access' ? 'Full Access' : 'Visual Only'}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                          @{user.username} | Logins: {user.login_count || 0}{user.max_logins ? `/${user.max_logins}` : ''}
                          {user.expires_at ? ` | Expires: ${new Date(user.expires_at).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => handleToggleDisable(user)} title={user.disabled ? 'Enable' : 'Disable'}
                        className={`px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${
                          user.disabled ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-400 hover:text-red-500 hover:bg-red-50'
                        }`} data-testid={`user-toggle-${user.id}`}>
                        {user.disabled ? 'Enable' : 'Disable'}
                      </button>
                      <button onClick={() => startEdit(user)} className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg" data-testid={`user-edit-${user.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(user)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`user-delete-${user.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
